<script lang="ts">
	import type { CityMapSummary } from '$lib/api/velatlas/response.types';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import { swipeClose } from '$lib/attachments/swipeClose';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import ControllablePopup, { PopupContent } from '$lib/components/ui/map/popup/ControllablePopup.svelte';
	import { PopupController } from '$lib/components/ui/map/popup/popup.controller.svelte';
	import type { PopupContentProps } from '$lib/components/ui/map/popup/popupProps';
	import TypesFilterPanel, { type TypeFilterOption, type TypeKey } from '$lib/components/ui/map/TypesFilterPanel.svelte';
	import WikiExplore, { ToggleWikiBtn } from '$lib/components/ui/map/WikiExplore.svelte';
	import { ViewStateController } from '$lib/controllers/lastView/viewState.controller';
	import {
		flattenCoordinates,
		lngLatToCoordinates,
		type Coordinates,
		type FlatCoordinates
	} from '$lib/features/_other/coordinates.type';
	import {
		CITY_FEATURE_TYPE_COLOURS,
		CITY_FEATURE_TYPE_ICON_MAP,
		CITY_FEATURE_TYPE_LABELS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import { ANALYTICS_EVENT_NAMES, FULL_SITE_URL, MAP_LIBRE_STYLE, PERSISTED_STATE_VISITED_CITIES, SITE_NAME } from '$lib/statics';
	import { useBackButtonInterception } from '$lib/utils/history.util.svelte';
	import { assignMissingStyleImageResolver } from '$lib/utils/maplibre.util';
	import { isSaveDataEnabled } from '$lib/utils/navigator.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import { getAllTypeFeatures } from '$remote/getAllTypeFeatures.remote';
	import { getCityMapSummary } from '$remote/getCityMapSummary.remote';
	import type { Snapshot } from '@sveltejs/kit';
	import type { Feature, Point } from 'geojson';
	import { Map as LibreMap } from 'maplibre-gl';
	import { PersistedState, watch } from 'runed';
	import Supercluster, {
		type ClusterProperties as BaseClusterProperties,
		type ClusterFeature,
		type PointFeature
	} from 'supercluster';
	import { type Component } from 'svelte';
	import {
		AttributionControl,
		CustomControl,
		FullScreenControl,
		GeolocateControl,
		GlobeControl,
		MapLibre,
		Marker,
		NavigationControl,
		Projection
	} from 'svelte-maplibre-gl';
	import 'svelte-maplibre-gl/vite';
	import { SvelteSet } from 'svelte/reactivity';
	import { fly } from 'svelte/transition';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconClose from '~icons/mdi/close';
	import IconDotsHorizontal from '~icons/mdi/dots-horizontal';
	import IconEarth from '~icons/mdi/earth';
	import IconMapMarkerPath from '~icons/mdi/map-marker-path';
	import IconPassport from '~icons/mdi/passport';
	import IconRefresh from '~icons/mdi/refresh';
	import type { Output, TravellerCard } from './+page.server';

	interface CustomClusterProperties {
		visited_count?: number;
	}
	type ClusterProperties = BaseClusterProperties & CustomClusterProperties;
	interface FeatureProps {
		id: string;
		label: string;
		slug: string;
		countryLabel: string;
		countrySlug: string;
		cityLabel: string;
		citySlug: string;
		href: string;
		thumbnailFilename: string | null;
		visited: boolean;
		isCity: boolean;
		type: CityFeatureType;
		coords: Coordinates;
	}
	type ClusterItem = ClusterFeature<ClusterProperties> | PointFeature<FeatureProps>;

	const VISITED_COLOURS = { visited: '#166534', notVisited: '#991b1b' };
	const visitedCities = new PersistedState<SvelteSet<string>>(PERSISTED_STATE_VISITED_CITIES, new SvelteSet<string>([]), {
		serializer: {
			serialize: (value) => JSON.stringify(Array.from(value)),
			deserialize: (value) => new SvelteSet(JSON.parse(value))
		}
	});
	const analyticsApi = new ClientVelatlasLoggerApi();

	const { data }: { data: { output: Output } } = $props();

	let map = $state<LibreMap | undefined>(undefined);
	let viewStateController = $state<ViewStateController | null>(null);
	let popoverController = $state<PopupController<FeatureProps> | undefined>(undefined);
	let cityFirstTimeVisited = $state<boolean>(false);
	let selectedCity = $state<FeatureProps | null>(null);
	let wikiMarkersEnabled = $state<boolean>(!isSaveDataEnabled());
	let wikiExplorePagesLoading = $state<boolean>(false);
	let bounds = $state<[number, number, number, number]>([-180, -90, 180, 90]);
	let mapZoom = $state<number>(2.5);
	let selectedFilterType = $state<TypeKey>(CityFeatureType.CITY_DETAILS);

	const output = $derived<Output>(data.output);
	const activeLayer = $derived(await loadLayer(selectedFilterType));
	const activeFeatures = $derived(activeLayer.features);
	let citySummary = $derived<CityMapSummary | null>(selectedCity ? await getCityMapSummary({ cityId: selectedCity.id }) : null);
	const filterOptions = $derived.by<TypeFilterOption[]>(() => {
		const counts: TypeFilterOption[] = [];
		for (const type of Object.keys(CityFeatureType) as CityFeatureType[]) {
			const count: number = output.filteringCounters[type] || 0;
			if (count === 0) continue;
			counts.push({ type, label: CITY_FEATURE_TYPE_LABELS[type] || type, count });
		}
		return counts;
	});
	const visitedCitiesCount = $derived<number>(visitedCities.current.size);
	const rawPoints = $derived.by<Feature<Point, FeatureProps>[]>(() => {
		const isCityLayer = activeLayer.type === CityFeatureType.CITY_DETAILS;

		return activeLayer.features.map((item) => ({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: flattenCoordinates(item.coords) },
			properties: {
				id: item.id,
				label: item.label,
				slug: item.slug,
				countryLabel: item.country.label,
				countrySlug: item.country.slug,
				cityLabel: item.city.label,
				citySlug: item.city.slug,
				href: UrlUtil.toCityFeature({
					countrySlug: item.country.slug,
					citySlug: item.city.slug,
					type: item.type,
					featureSlug: item.slug
				}).href,
				thumbnailFilename: item.thumbnailFilename,
				visited: isCityLayer && visitedCities.current.has(item.id),
				isCity: isCityLayer,
				type: item.type,
				coords: item.coords
			}
		}));
	});
	const index = $derived.by(() => {
		const sc = new Supercluster<FeatureProps, CustomClusterProperties>({
			radius: 40,
			maxZoom: 15,
			map: (props) => ({
				visited_count: props.visited ? 1 : 0
			}),
			reduce: (accumulated, props) => {
				accumulated.visited_count = (accumulated.visited_count || 0) + (props.visited_count || 0);
			}
		});

		sc.load(rawPoints);
		return sc;
	});
	const clusters = $derived.by<ClusterItem[]>(() => index.getClusters(bounds, Math.floor(mapZoom)));
	const combinedJsonLd = $derived(data.output.jsonLdBreadcrumbs);
	const meta = {
		title: `Atlas, The Interactive Global Map - ${SITE_NAME}`,
		description: 'Explore all our city destinations on an interactive global map.',
		canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toAtlas().href}`,
		imageUrl: UrlUtil.getStaticFileFullUrl('images', 'scenic-coastal-city-travel-destinations-brazil-banner.jpg')
	};

	useBackButtonInterception({
		stateKey: 'selectedCity',
		active: () => selectedCity !== null,
		enabled: () => deviceScreen.smallerThanDesktop,
		getData: () => selectedCity,
		onBack: clearSelectedCity,
		onForward: (city) => {
			if (city !== null) {
				handleMarkerClick(city, flattenCoordinates(city.coords));
			}
		}
	});

	$effect(() => {
		if (map) {
			// assign new map object whenever the map changes. This is because we use Snapshot, so it needs to be updated upon restoring the state. Without it, all of this could be done onload of maplibre
			if (viewStateController === null) {
				viewStateController = new ViewStateController(map, {
					initialState: { center: { longitude: 11, latitude: 51 }, zoom: 2.5, pitch: 0, bearing: 0 }
				});
			} else {
				viewStateController.update(map);
			}

			// Capture map state changes manually for Supercluster tracking
			const updateState = () => {
				if (!map) return;
				mapZoom = map.getZoom();
				const b = map.getBounds();
				bounds = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
			};

			map.on('moveend', updateState);
			map.on('zoomend', updateState);
			map.on('load', updateState);

			updateState();

			return () => {
				map?.off('moveend', updateState);
				map?.off('zoomend', updateState);
				map?.off('load', updateState);
			};
		}
		return () => {};
	});

	watch(
		() => selectedFilterType,
		(curr, prev) => {
			const isInitialLoad = prev === undefined;
			if (!isInitialLoad) {
				analyticsApi.sendAnalytics({ event: ANALYTICS_EVENT_NAMES.featureTypeMapFilter, payload: { type: curr } });
			}
		}
	);

	watch(
		() => selectedCity,
		(curr, prev) => {
			const isInitialLoad = prev === undefined;
			if (!isInitialLoad && curr !== null) {
				analyticsApi.sendAnalytics({
					event: ANALYTICS_EVENT_NAMES.cityViewed,
					payload: { country: curr.countrySlug, city: curr.citySlug }
				});
			}
		}
	);

	async function loadLayer(type: TypeKey) {
		const cityFeatureType = type === 'ALL' ? CityFeatureType.CITY_DETAILS : type;
		const features = await getAllTypeFeatures({ cityFeatureType });
		return { type: cityFeatureType, features };
	}

	function resetVisitedCities(): void {
		if (confirm('Reset your visited cities?')) {
			visitedCities.current = new SvelteSet<string>([]);
		}
	}

	function handleMarkerClick(props: FeatureProps, coords: FlatCoordinates): void {
		viewStateController?.add();

		if (props.isCity) {
			selectedCity = props;
			if (!visitedCities.current.has(props.id)) {
				visitedCities.current = new SvelteSet([...visitedCities.current, props.id]);
				cityFirstTimeVisited = true;
				setTimeout(() => (cityFirstTimeVisited = false), 2500);
			}
			flyToCity(lngLatToCoordinates(coords));
		} else {
			selectedCity = null;
			popoverController?.openPopup('click', props, lngLatToCoordinates(coords));
			map?.flyTo({
				center: coords,
				zoom: 16,
				pitch: 60,
				bearing: 0,
				speed: 2.5,
				curve: 1.5,
				essential: true
			});
		}
	}

	function clearSelectedCity(): void {
		selectedCity = null;
		viewStateController?.restore();
		popoverController?.closePopup(undefined);
	}

	function flyToCity(coords: Coordinates): void {
		map?.flyTo({
			center: flattenCoordinates(coords),
			zoom: 14,
			pitch: 60,
			bearing: 0,
			padding: { top: 0, bottom: 0, left: 0, right: 0 },
			speed: 1.2,
			curve: 1.5,
			essential: true
		});
	}

	function handlePopupClose(): void {
		const doAfter = () => viewStateController?.restore();
		popoverController?.closePopup(undefined, { doAfter: doAfter });
	}

	export const snapshot: Snapshot<{
		selectedType: TypeKey;
		selectedCity: FeatureProps | null;
		citySummary: CityMapSummary | null;
		cameraState: { center: { lng: number; lat: number }; zoom: number; pitch: number; bearing: number } | null;
	}> = {
		capture: () => {
			return {
				selectedType: selectedFilterType,
				selectedCity,
				citySummary,
				cameraState: map
					? {
							center: { lng: map.getCenter().lng, lat: map.getCenter().lat },
							zoom: map.getZoom(),
							pitch: map.getPitch(),
							bearing: map.getBearing()
						}
					: null
			};
		},
		restore: (value) => {
			selectedFilterType = value.selectedType;
			selectedCity = value.selectedCity;
			citySummary = value.citySummary;
			if (value.cameraState && map) map.jumpTo(value.cameraState);
			if (selectedCity) flyToCity(selectedCity.coords);
		}
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`Interactive Travel Atlas`}
	description={`Explore our curated collection of destinations across the globe. As you move around the map, click the dynamic article
				icons to read nearby Wikipedia entries and uncover notable sights along the way.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		coordinates: activeFeatures.map((item) => item.coords).slice(0, 15)
	}}
/>

<div class="container mx-auto px-4 py-8 lg:px-0">
	<figure class="relative h-[75vh] w-full overflow-hidden lg:h-[85vh]" aria-label="Map of all cities we have data for">
		<MapLibre
			bind:map
			class="@container-size relative z-0 h-full w-full overflow-hidden"
			style={MAP_LIBRE_STYLE}
			center={{ lng: 11, lat: 51 }}
			zoom={2.5}
			minZoom={2.5}
			pitch={0}
			bearing={0}
			attributionControl={false}
			renderWorldCopies={true}
			onload={(e) => {
				assignMissingStyleImageResolver(e.target);
				popoverController = new PopupController<FeatureProps>();
			}}
		>
			<div
				class="pointer-events-none absolute top-4 bottom-4 left-4 z-70 flex w-72 max-w-[calc(100vw-2rem)] flex-col gap-3 text-base-content"
			>
				{@render Passport()}
				<TypesFilterPanel
					options={filterOptions}
					bind:selectedType={selectedFilterType}
					onTypeSelect={(_: TypeKey) => {
						selectedCity = null;
					}}
				/>
			</div>

			{#if selectedCity}
				<div
					{@attach swipeClose({ onClose: clearSelectedCity, enabled: deviceScreen.isMobile })}
					class="fixed right-0 bottom-0 left-0 z-120 flex max-h-[85vh] w-full flex-col overflow-hidden overscroll-contain rounded-t-box border border-base-200/80 bg-base-200 text-base-content shadow-2xl transition-transform duration-300 md:absolute md:top-4 md:right-4 md:bottom-4 md:left-auto md:max-h-none md:w-[35vw] md:rounded-box lg:w-[22vw]"
					role="dialog"
					aria-modal="false"
					aria-label="City Details"
					in:fly={{
						x: deviceScreen.isMobile ? 0 : 20,
						y: deviceScreen.isMobile ? 100 : 0,
						duration: 300
					}}
					out:fly={{ y: deviceScreen.isMobile ? 100 : 0, duration: 200 }}
				>
					{#if citySummary}
						{@render SelectedCity(selectedCity, citySummary)}
					{/if}
				</div>
			{/if}

			<NavigationControl showCompass={false} position="top-right" />
			<GlobeControl position="top-right" />
			<GeolocateControl positionOptions={{ enableHighAccuracy: true }} position="top-right" />
			<CustomControl position="top-right">
				{@render ToggleWikiBtn({
					isEnabled: wikiMarkersEnabled,
					isLoadingPages: wikiExplorePagesLoading,
					onToggle: (isEnabled) => (wikiMarkersEnabled = isEnabled)
				})}
			</CustomControl>
			<FullScreenControl position="top-right" />
			<AttributionControl compact={true} position="bottom-right" />

			<Projection type="globe" />

			{#if activeLayer.type === CityFeatureType.CITY_DETAILS && citySummary?.featureCoordinates}
				{#each citySummary.featureCoordinates as feat}
					{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[feat.type])}
					{const colour = $derived<string>(CITY_FEATURE_TYPE_COLOURS[feat.type])}
					<Marker lnglat={flattenCoordinates(feat.coordinates)} class="z-30">
						{#snippet content()}
							<div
								class="relative flex size-10 items-center justify-center rounded-full bg-neutral-900 text-xs font-extrabold text-white shadow-lg transition-transform hover:scale-110 md:size-12"
								style="border: 3px solid {colour}"
							>
								<div class="scale-110 opacity-90"><Icon /></div>
							</div>
						{/snippet}
					</Marker>
				{/each}
			{/if}

			{#each clusters as feature (feature.id || (feature.properties as FeatureProps).id)}
				{const isCluster = $derived<boolean>('cluster' in feature.properties && !!feature.properties.cluster)}
				{#if isCluster}
					{const cluster = $derived<ClusterFeature<ClusterProperties>>(feature as ClusterFeature<ClusterProperties>)}
					{const props = $derived<ClusterProperties>(cluster.properties)}
					<Marker
						class="z-30"
						lnglat={{ lng: cluster.geometry.coordinates[0], lat: cluster.geometry.coordinates[1] }}
						onclick={() =>
							map?.easeTo({
								center: { lng: cluster.geometry.coordinates[0], lat: cluster.geometry.coordinates[1] },
								zoom: Math.min(17, index.getClusterExpansionZoom(props.cluster_id) + 0.5),
								essential: true
							})}
					>
						{#snippet content()}
							{@render ClusterMarker(props)}
						{/snippet}
					</Marker>
				{:else}
					{const props = $derived<FeatureProps>(feature.properties as FeatureProps)}
					{const coords = $derived<FlatCoordinates>(feature.geometry.coordinates as FlatCoordinates)}
					<Marker class="z-31" lnglat={coords} onclick={() => handleMarkerClick(props, coords)}>
						{#snippet content()}
							{@render FeatureMarker(props, coords)}
						{/snippet}
					</Marker>
				{/if}
			{/each}

			{#if popoverController}
				<ControllablePopup controller={popoverController} overrideHandleClose={handlePopupClose}>
					{#snippet ContentSnippet()}
						{const featureProps = $derived<FeatureProps | undefined>(popoverController!.popup.props)}
						{const popupContentProps = $derived.by<PopupContentProps | undefined>(() => {
							if (featureProps === undefined) {
								return undefined;
							}
							const badges = featureProps.isCity ? [] : [`${featureProps.cityLabel}, ${featureProps.countryLabel}`];
							return {
								image: {
									filename: featureProps.thumbnailFilename,
									IconOnNoImage: CITY_FEATURE_TYPE_ICON_MAP[featureProps.type]
								},
								label: featureProps.isCity ? `${featureProps.label}, ${featureProps.countryLabel}` : featureProps.label,
								badges: badges,
								cta: {
									label: 'Learn More',
									href: featureProps.href
								}
							};
						})}
						{@render PopupContent(popupContentProps)}
					{/snippet}
				</ControllablePopup>
			{/if}

			{#if map && wikiMarkersEnabled && viewStateController}
				<WikiExplore
					{map}
					parentViewStateController={viewStateController}
					onLoadingPagesChange={(isLoading) => (wikiExplorePagesLoading = isLoading)}
				/>
			{/if}
		</MapLibre>
		<figcaption class="sr-only">
			Interactive map showing locations of {output.citiesCounter} cities all over the world, including
			{output.exampleCityNames.join(', ')}, and many more.
		</figcaption>
	</figure>

	{@render TravellerSection()}
</div>

{#snippet Passport()}
	{const hasVisitedAll = $derived<boolean>(visitedCitiesCount === output.citiesCounter)}
	<div class="pointer-events-auto flex items-center gap-2">
		<div
			class={{
				'badge badge-lg shadow-lg transition-all duration-300': true,
				'scale-110 badge-success': cityFirstTimeVisited,
				'backdrop-blur-md badge-info': !cityFirstTimeVisited && !hasVisitedAll,
				'badge-success': hasVisitedAll
			}}
		>
			<IconPassport class={{ 'text-lg': true, 'animate-bounce': cityFirstTimeVisited, 'text-primary': !cityFirstTimeVisited }} />
			<span class="font-mono font-bold">{visitedCitiesCount}/{output.citiesCounter}</span>
			<span class="hidden text-xs font-medium opacity-70 sm:inline">Cities Visited</span>
		</div>

		{#if visitedCitiesCount > 0}
			<button
				class="btn btn-circle bg-base-100/50 btn-ghost text-base-content/70 shadow-sm backdrop-blur-md transition-colors btn-xs hover:bg-base-100 hover:text-error"
				onclick={resetVisitedCities}
				title="Reset Passport"
			>
				<IconRefresh class="text-xs" />
			</button>
		{/if}
	</div>
{/snippet}

{#snippet SelectedCity(city: FeatureProps, summary: CityMapSummary)}
	{@render CloseBtn()}

	<div class="relative h-40 shrink-0 bg-base-300">
		{@render ImageHeader()}
	</div>

	<div class="flex min-h-0 flex-1 flex-col overflow-hidden p-5">
		<div class="mb-4 shrink-0">
			{@render Intro()}
		</div>
		<div class="flex min-h-0 flex-1 flex-col">
			{@render SummaryData()}
		</div>
	</div>

	{#snippet CloseBtn()}
		<button
			class="btn absolute top-2 right-2 z-30 btn-circle border-none bg-black/20 btn-ghost text-white btn-sm hover:bg-black/40"
			aria-label="Close sidebar"
			onclick={clearSelectedCity}
		>
			<IconClose />
		</button>
	{/snippet}

	{#snippet ImageHeader()}
		{const imageUrl = $derived<string | null>(city.thumbnailFilename ? UrlUtil.getFullImageUrl(city.thumbnailFilename) : null)}
		{#if imageUrl}
			<img class="h-full w-full object-cover" src={imageUrl} alt="" draggable="false" />
			<div class="absolute inset-0 bg-linear-to-t from-base-200 via-transparent to-transparent"></div>
		{/if}
	{/snippet}

	{#snippet Intro()}
		<div class="flex flex-col gap-4">
			<div class="flex flex-col gap-2">
				<h2 class="text-3xl leading-none font-extrabold tracking-tight text-base-content md:text-4xl">
					{city.label}
				</h2>

				<div class="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
					{const countryNav = $derived<NavLink>(UrlUtil.toCountry({ countrySlug: city.countrySlug }))}
					<a
						class="badge gap-1.5 border-none badge-outline bg-primary/5 px-3.5 py-3 font-bold transition-all badge-primary hover:bg-primary hover:text-primary-content"
						href={`/${countryNav.href}`}
						data-sveltekit-preload-data={countryNav.preloadData}
						data-sveltekit-preload-code={countryNav.preloadCode}
					>
						<IconEarth />
						{city.countryLabel.toUpperCase()}
					</a>

					<span class="text-base-content/20 select-none">•</span>

					<span class="flex items-center gap-1.5 font-medium tracking-wide text-base-content/70">
						FOUNDED IN {summary.foundedYear}
					</span>
				</div>
			</div>

			<a class="group btn w-full rounded-full shadow-md btn-primary" href={`/${city.href}`}>
				Explore {city.label}
				<IconArrowRight class="transition-transform group-hover:translate-x-1" />
			</a>
		</div>
	{/snippet}

	{#snippet SummaryData()}
		{const typesToDisplay: CityFeatureType[] = [
			CityFeatureType.ANNUAL_FESTIVAL,
			CityFeatureType.GREEN_SPACE,
			CityFeatureType.UNESCO_SITE,
			CityFeatureType.HIDDEN_GEM,
			CityFeatureType.ATTRACTION,
			CityFeatureType.FOOD
		]}
		{const scenicTypes: CityFeatureType[] = [
			CityFeatureType.SCENIC_STROLL,
			CityFeatureType.SCENIC_BIKE_RIDE,
			CityFeatureType.SELF_GUIDED_TOUR
		]}
		{const tourCount = $derived<number>(scenicTypes.reduce((sum, type) => sum + (summary.featureCounts[type] || 0), 0))}
		{const otherCount = $derived<number>(
			Object.entries(summary.featureCounts).reduce((sum, [key, val]) => {
				const type = key as CityFeatureType;
				if (!typesToDisplay.includes(type) && !scenicTypes.includes(type)) {
					return sum + val;
				}
				return sum;
			}, 0)
		)}

		<div class="mb-4 flex flex-wrap gap-2 border-b border-base-content/5 pb-4">
			{#each typesToDisplay as type (type)}
				{const counter = $derived<number | undefined>(summary.featureCounts[type])}
				{#if counter !== undefined && counter > 0}
					{@render TypeTooltip(CITY_FEATURE_TYPE_LABELS[type], counter, CITY_FEATURE_TYPE_ICON_MAP[type])}
				{/if}
			{/each}

			{#if tourCount > 0}
				{@render TypeTooltip('Scenic Routes', tourCount, IconMapMarkerPath)}
			{/if}

			{#if otherCount > 0}
				{@render TypeTooltip('Other Activities', otherCount, IconDotsHorizontal)}
			{/if}
		</div>

		<div
			class="min-h-0 flex-1 touch-pan-y scrollbar-thin scrollbar-thumb-base-content/20 scrollbar-track-transparent overflow-y-auto pr-1 hover:scrollbar-thumb-base-content/40"
		>
			<p class="text-sm leading-relaxed font-medium text-base-content/70">
				{summary.vibe}
			</p>
		</div>

		{#snippet TypeTooltip(label: string, count: number, Icon: Component)}
			<div class="tooltip tooltip-bottom" data-tip="{count} {label}">
				<div class="badge flex badge-lg badge-secondary">
					<Icon />
					<span class="text-secondary-content/70">{count}</span>
				</div>
			</div>
		{/snippet}
	{/snippet}
{/snippet}

{#snippet ClusterMarker(props: ClusterProperties)}
	{const colour = $derived.by<string>(() => {
		if (activeLayer.type === CityFeatureType.CITY_DETAILS) {
			return props.visited_count === props.point_count ? VISITED_COLOURS.visited : VISITED_COLOURS.notVisited;
		}
		return CITY_FEATURE_TYPE_COLOURS[activeLayer.type];
	})}
	<button
		class="relative flex size-11 cursor-pointer items-center justify-center rounded-full border-3 bg-primary text-xs font-black text-primary-content transition-all hover:scale-110 active:scale-95 md:size-18 lg:size-13"
		style:border-color={colour}
		type="button"
	>
		{props.point_count_abbreviated || props.point_count}
	</button>
{/snippet}

{#snippet FeatureMarker(props: FeatureProps, coords: FlatCoordinates)}
	{const colour = $derived<string>(
		props.isCity ? (props.visited ? VISITED_COLOURS.visited : VISITED_COLOURS.notVisited) : CITY_FEATURE_TYPE_COLOURS[props.type]
	)}
	{const imageUrl = $derived<string | null>(props.thumbnailFilename ? UrlUtil.getFullImageUrl(props.thumbnailFilename) : null)}
	{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[props.type])}
	<button
		class="relative flex size-11 cursor-pointer items-center justify-center rounded-full border-3 bg-neutral text-neutral-content transition-transform hover:scale-110 md:size-18 lg:size-13"
		style:border-color={colour}
		aria-label={props.label}
		onmousemove={() => popoverController?.openPopup('hover', props, lngLatToCoordinates(coords))}
		onmouseleave={() => popoverController?.closePopup('hover')}
	>
		{#if imageUrl}
			<img src={imageUrl} class="h-full w-full rounded-full object-cover" alt={props.label} />
		{:else}
			<Icon class="text-xl font-extrabold" />
		{/if}
	</button>
{/snippet}

{#snippet TravellerSection()}
	<section class="bg-base-100 px-6 py-12 text-base-content md:px-10">
		<div class="mb-12 max-w-xl">
			<p class="mb-3 text-xs tracking-[0.14em] text-base-content/70">FIND YOUR FIT</p>
			<h2 class="mb-3 text-3xl leading-tight font-extrabold md:text-4xl">What kind of traveller are you?</h2>
			<p class="text-sm leading-relaxed text-base-content/70">
				Start with a mood, not a destination. Find cities that match how you like to travel.
			</p>
		</div>

		<div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
			{#each output.travellers as card (card.tag)}
				<article
					class="group card overflow-hidden bg-base-100 shadow-sm transition-all duration-300 card-border hover:bg-base-200/30"
				>
					{@render ImagesGrid(card)}

					<div class="card-body gap-4 p-6">
						<h3 class="card-title text-lg font-bold text-base-content">
							{card.title}
						</h3>
						<p class="-mt-1 text-sm leading-relaxed text-base-content/70">
							{card.description}
						</p>

						{@render CityChips(card)}
						{@render CTA(card)}
					</div>
				</article>
			{/each}
		</div>
	</section>

	{#snippet ImagesGrid(card: TravellerCard)}
		<div class="grid aspect-4/3 shrink-0 grid-cols-2 gap-0.5">
			{#each card.images as img (img.filename)}
				<div class="relative overflow-hidden">
					<img
						class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.1]"
						src={UrlUtil.getFullImageUrl(img.filename)}
						alt={img.altText}
						width={img.width}
						height={img.height}
						loading="lazy"
						decoding="async"
					/>
				</div>
			{/each}
		</div>
	{/snippet}

	{#snippet CityChips(card: TravellerCard)}
		<div class="flex flex-wrap gap-1.5">
			{#each card.cities as city (city.href)}
				<a
					class="inline-block rounded-full border border-base-content/10 bg-base-content/5 px-3 py-1 text-xs font-semibold text-base-content/80 transition-all duration-150 select-none hover:border-primary/20 hover:bg-primary/10 hover:text-primary"
					href={`/${city.href}`}
				>
					{city.label}
				</a>
			{/each}
		</div>
	{/snippet}

	{#snippet CTA(card: TravellerCard)}
		<div class="mt-auto card-actions w-full justify-center pt-2 md:justify-end">
			<a
				class="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-primary"
				href={`/${UrlUtil.toCities({ filters: { tags: [card.tag] } }).href}`}
			>
				{card.ctaLabel}
				<IconArrowRight class="transition-transform group-hover:translate-x-1" />
			</a>
		</div>
	{/snippet}
{/snippet}
